// Fields (CONTRACT "Controls", "Inputs"): inset ground, soft outline, focus
// border in the focus blue. Field takes an optional leading icon, a trailing
// unit ("stages", "MC USD") and a mono mode for contracts. SearchField is the
// top bar search with the "/" key cap; when slashFocus is on, pressing "/" or
// Cmd/Ctrl K anywhere (outside a text field) focuses it.
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { Eye, EyeSlash, MagnifyingGlass } from '@phosphor-icons/react';
import { cx } from './cx';
import { KeyCap } from './KeyCap';
import { isTextInput } from './isTextInput';
import './Input.css';

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  readonly icon?: ReactNode;
  readonly unit?: string;
  readonly mono?: boolean;
  readonly size?: 'md' | 'sm';
  readonly invalid?: boolean;
  readonly wrapperClassName?: string;
  readonly trailing?: ReactNode;
  // Adds a control that switches the field between hidden and readable.
  // For password fields only; ignored on any other type.
  readonly revealable?: boolean;
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { icon, unit, mono = false, size = 'md', invalid = false, wrapperClassName, className, trailing, revealable = false, type, ...rest },
  ref,
) {
  const [revealed, setRevealed] = useState(false);
  const canReveal = revealable && type === 'password';
  return (
    <label className={cx('field', size === 'sm' && 'field-sm', mono && 'field-mono', invalid && 'field-invalid', wrapperClassName)}>
      {icon ? <span className="field-ic" aria-hidden="true">{icon}</span> : null}
      <input
        ref={ref}
        type={canReveal && revealed ? 'text' : type}
        className={cx('field-in', className)}
        aria-invalid={invalid || undefined}
        {...rest}
      />
      {unit ? <span className="field-unit">{unit}</span> : null}
      {/* The workspace asks for 14 characters or more and every password
          screen made the reader type them blind. The control is a button, not
          a checkbox, so it never enters the form's own value, and its state
          lives in aria-pressed rather than in the icon alone. */}
      {canReveal ? (
        <button
          type="button"
          className="field-reveal"
          aria-pressed={revealed}
          aria-label={revealed ? 'Hide the password' : 'Show the password'}
          title={revealed ? 'Hide the password' : 'Show the password'}
          onClick={() => setRevealed((current) => !current)}
        >
          {revealed ? <EyeSlash aria-hidden="true" /> : <Eye aria-hidden="true" />}
        </button>
      ) : null}
      {trailing}
    </label>
  );
});

export interface SearchFieldProps extends Omit<FieldProps, 'icon' | 'trailing' | 'unit'> {
  readonly slashFocus?: boolean;
}

export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(function SearchField(
  // Default placeholder matches the reference chrome (shell.html search).
  { slashFocus = false, wrapperClassName, placeholder = 'Paste a contract, ticker, X handle, or caller', ...rest },
  ref,
) {
  const inner = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inner.current as HTMLInputElement);

  useEffect(() => {
    if (!slashFocus) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      const cmdK = event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey);
      const slash = event.key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey;
      if (!cmdK && !slash) return;
      if (slash && isTextInput(event.target)) return;
      event.preventDefault();
      inner.current?.focus();
      inner.current?.select();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [slashFocus]);

  return (
    <Field
      ref={inner}
      type="search"
      icon={<MagnifyingGlass />}
      placeholder={placeholder}
      wrapperClassName={cx('field-search', wrapperClassName)}
      trailing={<KeyCap className="field-kc">/</KeyCap>}
      {...rest}
    />
  );
});
